import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { School } from '../../../types/admin';
import { Plus } from 'lucide-react';
import { useSchool } from '../../../contexts/SchoolContext';
import toast from 'react-hot-toast';
import CreateSchoolForm from './CreateSchoolForm';
import JoinSchoolForm from './JoinSchoolForm';
import SchoolList from './SchoolList';
import { useSchoolActions } from './useSchoolActions';

interface SchoolSelectorProps {
  onClose: () => void;
}

export default function SchoolSelector({ onClose }: SchoolSelectorProps) {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { selectedSchool } = useSchool();
  const { 
    loading: actionLoading, 
    handleJoinSchool, 
    handleCreateSchool, 
    handleSelectSchool 
  } = useSchoolActions();

  useEffect(() => {
    const loadSchools = async () => {
      console.log('[SchoolSelector] Starting loadSchools...');
      try {
        const user = auth.currentUser;
        if (!user) {
          console.log('[SchoolSelector] No current user found.');
          setLoading(false);
          return;
        }
        console.log('[SchoolSelector] Current user ID:', user.uid);
        
        const membershipQuery = query(
          collection(db, 'school_members'),
          where('userId', '==', user.uid)
        );
        const membershipSnapshot = await getDocs(membershipQuery);
        console.log('[SchoolSelector] Membership snapshot size:', membershipSnapshot.size);

        const schoolData: School[] = [];
        for (const memberDoc of membershipSnapshot.docs) {
          console.log('[SchoolSelector] Processing membership doc:', memberDoc.id, memberDoc.data());
          const schoolId = memberDoc.data().schoolId;
          console.log('[SchoolSelector] Fetching school with ID:', schoolId);
          const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
          if (schoolDoc.exists()) {
            console.log('[SchoolSelector] Found school:', schoolDoc.id, schoolDoc.data());
            schoolData.push({ id: schoolDoc.id, ...schoolDoc.data() } as School);
          } else {
            console.warn('[SchoolSelector] No school found for membership doc:', memberDoc.id);
          }
        }
        console.log('[SchoolSelector] Finished loading schools. Total schools:', schoolData.length);
        setSchools(schoolData);
      } catch (error) {
        console.error('[SchoolSelector] Error loading schools:', error);
        toast.error('Failed to load schools');
      } finally {
        setLoading(false);
      }
    };

    loadSchools();
  }, []);

  if (loading) {
    console.log('[SchoolSelector] Loading state active...');
    return (
      <div className="p-4">
        <div className="text-text-secondary">Loading schools...</div>
      </div>
    );
  }

  if (showCreateForm) {
    console.log('[SchoolSelector] Showing create school form.');
    return (
      <CreateSchoolForm
        onCancel={() => {
          console.log('[SchoolSelector] Cancel create school form.');
          setShowCreateForm(false);
        }}
        onCreate={async (school) => {
          console.log('[SchoolSelector] onCreate callback invoked with school data:', school);
          await handleCreateSchool(school);
          onClose();
        }}
        loading={actionLoading}
      />
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-4">Select School</h2>

      <JoinSchoolForm
        onJoin={async (code) => {
          console.log('[SchoolSelector] Join school form submitted with code:', code);
          await handleJoinSchool(code);
          onClose();
        }}
        loading={actionLoading}
      />

      <button
        onClick={() => {
          console.log('[SchoolSelector] Switching to create school form.');
          setShowCreateForm(true);
        }}
        className="w-full p-4 bg-surface-dark rounded-lg hover:bg-primary-50 transition-colors 
                 flex items-center gap-3 mb-4"
      >
        <div className="p-2 bg-primary-100 rounded-lg">
          <Plus className="w-5 h-5 text-primary-600" />
        </div>
        <span className="font-medium text-text-primary">Create New School</span>
      </button>

      <SchoolList
        schools={schools}
        selectedSchool={selectedSchool}
        onSelectSchool={async (school) => {
          console.log('[SchoolSelector] onSelectSchool invoked for school:', school.id, school);
          await handleSelectSchool(school);
          onClose();
        }}
      />
    </div>
  );
}
